import React, { PropsWithChildren, useEffect, useState } from 'react';
import { WrapRootElementBrowserArgs } from 'gatsby';
import { Helmet } from 'react-helmet';
import {
  atom,
  MutableSnapshot,
  RecoilRoot,
  selector,
  useRecoilState,
  useRecoilValue
} from 'recoil';
import RecoilNexus from 'recoil-nexus'
import { theme, ThemeProvider } from '@dls/web';
import QueryString from 'query-string';
import { useMedia } from 'use-media';
import '@dls/web/assets/fonts.css';
import { createGlobalStyle } from 'styled-components';
import merge from 'lodash/merge';
import clone from 'lodash/cloneDeep';
import FingerprintJS from '@wec-core/fingerprintjs';
import './index.css';
import media, { breakpoints } from './helpers/media';
import Api from './helpers/Api';
import Spinner from './components/Spinner';
import { AemConfigResponse, FlowConfig } from './helpers/RequestResponse';
import { getThemeNameFromConfig, getThemeBackground } from './helpers/theme';
import { verifyOrSetAgentCookie } from './helpers/utils';

interface GlobalStyleProps {
  background: string;
}

interface ThemeStylesProps {
  themeName?: string;
}

const ThemeStyles = createGlobalStyle<ThemeStylesProps>`
  ${(p) => {
    if (p.themeName === 'GOMOSMB') {
      return `
      /* Primary button text color */
        button[class*='Button']:not([class*='secondary']):not([class*='Secondary']) span p {
          color: #E5FF17;
        }
      `;
    }
    return '';
  }}
`;

const GlobalStyle = createGlobalStyle<GlobalStyleProps>`
  ${(p) => {
    if (typeof window !== 'undefined' && window.location.pathname.includes('/login')) {
      return `
        html {
          background-color: grey;
          background-image: url('${p.background}');
          background-size: cover;
          background-position: center;
          overflow: hidden;
        }
      `;
    }
    return '';
  }}
  ${media.sm`
    div[class*='ModalContainer'] > div {
      width: 450px;
      padding: 0px;
      > div > div {
        padding: 0px;
        flex-basis: 100%;
        max-width: 100%;
      }
    }
  `}
  ${media.md`
    div[class*='ModalContainer'] > div {
      width: 460px;
      padding: 0px;
      > div > div {
        padding: 0px;
        flex-basis: 100%;
        max-width: 100%;
      }
    }
  `}
`;


export function RootWrapper(props: WrapRootElementBrowserArgs) {
  return (
    <RecoilRoot>
      <RecoilNexus/>
      <RootWrapperThemes>{props.element}</RootWrapperThemes>
    </RecoilRoot>
  );
}

const defaultAemConfig: AemConfigResponse = {
  background: '/sso/background.png',
  mode: 'light',
  onepassFlows: []
};

export const aemConfigState = atom<AemConfigResponse>({
  key: 'aemConfig',
  default: defaultAemConfig
});

export const flowConfigState = selector<FlowConfig>({
  key: 'flowConfig',
  get: ({ get }) => {
    let search = typeof window !== 'undefined' ? window.location.search : '';
    const qs = QueryString.parse(search);
    const flows = get(aemConfigState).onepassFlows || [];
    const flowId = qs.flowId || (qs.state && qs.state.split('--')[2]);
    return flowId ? flows.find((flow) => flow.flowId === flowId) : flows[0];
  }
});


// Theme name is derived from config + current search using helper for clarity.
export const themeNameState = selector<string | undefined>({
  key: 'themeName',
  get: ({ get }) => {
    const config = get(aemConfigState);
    const search = typeof window !== 'undefined' ? window.location.search : '';
    return getThemeNameFromConfig(config, search);
  }
});

export function RootWrapperThemes(props: React.PropsWithChildren<{}>) {
  const [config, setConfig] = useRecoilState(aemConfigState);
  const [loading, setLoading] = useState(true);
  // Derive theme from active flow portal instead of query param for scalability.
  const themeName = useRecoilValue(themeNameState);
  // Get theme-specific background image
  const backgroundImage = getThemeBackground(themeName);

  const isWide = useMedia({ minWidth: breakpoints.sm });

  let modifiedTheme = {};
  if (themeName === 'GOMOSMB') {
    // GOMO theme: override primary base color and white color
    modifiedTheme = merge({}, theme, {
      main: { 
        colours: { 
          primary: { base: '#4D28E8', d2: '#4D28E8' },
        } 
      }
    });
  } else if (isWide && config.mode === 'dark') {
    modifiedTheme = merge({}, theme, {
      main: { colours: { tertiary: { base: '#fff' } } }
    });
  } else {
    modifiedTheme = clone(theme);
  }

  useEffect(() => {
    const qs = QueryString.parse(window.location.search);
    const { onepassFlows } = config;
    if(onepassFlows
       && onepassFlows.length && !onepassFlows.find((flow) => flow.flowId === qs.flowId)
       && (window.location.pathname === '/' ||  window.location.pathname === '/sso/' || window.location.pathname.includes('/login'))) {
      window.location.href = `/login/?flowId=${onepassFlows[0].flowId}`;
    }
  }, [config]);

  useEffect(() => {
    if(typeof window !== undefined) {
      window.recaptchaOptions = {
        useRecaptchaNet: true
      }
    }
    const initConfig = async () => {
      const qs = QueryString.parse(window.location.search);
      const isUDF = (qs.flowId == '9762d9f3-55bf-4d60-8971-c739f7509da3');
      let payload = {cookies: {'max-age': '2592000'}};
      if(isUDF) {
        payload = { ...payload, excludeSources: ['privateIp']}
      }
      const fp = await FingerprintJS.load(payload)
      let { components, visitorId } = await fp.get();
      console.log(components);
      /*
        Use the agentId stored in the cookie to address 
        intermittent issues with agentId generation in the browser.
      */
      visitorId = verifyOrSetAgentCookie(visitorId);

      Api.aemConfig().then((res) => {
        const mappedData = res.data.onepassFlows.map((e) => ({ ...e, agentId: visitorId }));
        setConfig({...res.data, onepassFlows: mappedData});
        setLoading(false);
      });
    };
    initConfig();
  }, []);

  return (
    <>
      <Helmet>
        <title>Welcome to Singtel OnePass</title>
      </Helmet>
      <ThemeProvider theme={modifiedTheme}>
        <>
          <GlobalStyle background={backgroundImage} />
          <ThemeStyles themeName={themeName} />
          {loading ? <Spinner/> : props.children}
        </>
      </ThemeProvider>
    </>
  );
}

type Props = PropsWithChildren<{
  initializeState?: (mutableSnapshot: MutableSnapshot) => void;
}>;

export function TestRootWrapper(props: Props) {
  return (
    <RecoilRoot initializeState={props.initializeState}>
      <RecoilNexus/>
      <ThemeProvider theme={theme}>
        <>
          <GlobalStyle background={defaultAemConfig.background} />
          {props.children}
        </>
      </ThemeProvider>
    </RecoilRoot>
  );
}